import React, { useMemo } from "react";

const CONFETTI = ["#34D399", "#FBBF24", "#F472B6", "#60A5FA", "#F97316", "#A78BFA"];

function Confetti({ count = 28 }) {
  const bits = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left: `${(i * 37) % 100}%`, delay: `${(i % 9) * 0.35}s`, dur: `${3.2 + (i % 5) * 0.6}s`,
    dx: `${((i % 7) - 3) * 18}px`, color: CONFETTI[i % CONFETTI.length], round: i % 3 === 0,
  })), [count]);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {bits.map((b, i) => (
        <span key={i} className="ask-confetti" style={{ left: b.left, animationDelay: b.delay, animationDuration: b.dur, background: b.color, borderRadius: b.round ? 9999 : 2, "--dx": b.dx }} />
      ))}
    </div>
  );
}

function Playful({ to, from }) {
  return (
    <div data-testid="thanks-anim-playful" className="relative min-h-[100dvh] overflow-hidden bg-gradient-to-b from-emerald-50 via-teal-50 to-amber-50 flex flex-col items-center justify-center px-6 text-center">
      <Confetti />
      <div className="relative">
        <div className="ask-bounce flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-orange-400 shadow-[inset_-10px_-14px_0_rgba(0,0,0,0.12),0_24px_40px_rgba(234,88,12,0.35)]">
          <svg viewBox="0 0 100 100" className="h-28 w-28" aria-hidden>
            <ellipse cx="34" cy="40" rx="7" ry="9" fill="#1F2937" /><ellipse cx="66" cy="40" rx="7" ry="9" fill="#1F2937" />
            <circle cx="36" cy="37" r="2.4" fill="#fff" /><circle cx="68" cy="37" r="2.4" fill="#fff" />
            <path d="M24 58 Q50 88 76 58 Z" fill="#7F1D1D" /><path d="M36 70 Q50 80 64 70 Q50 74 36 70Z" fill="#FB7185" />
            <circle cx="20" cy="56" r="6" fill="#FB923C" opacity=".7" /><circle cx="80" cy="56" r="6" fill="#FB923C" opacity=".7" />
          </svg>
        </div>
        <span className="ask-wiggle absolute -left-10 top-6 text-5xl" aria-hidden>🙌</span>
        <span className="ask-wiggle absolute -right-10 top-4 text-5xl" style={{ animationDelay: ".3s" }} aria-hidden>🎉</span>
      </div>
      <h1 className="ask-rise mt-10 font-display text-4xl font-extrabold tracking-tight text-emerald-950" style={{ animationDelay: ".2s" }}>
        You're a legend{to ? `, ${to}` : ""}!
      </h1>
      <p className="ask-rise mt-3 text-base font-semibold text-emerald-900/70" style={{ animationDelay: ".4s" }}>{from} is doing a happy dance right now.</p>
    </div>
  );
}

function Friendly({ to, from }) {
  const hearts = useMemo(() => Array.from({ length: 16 }, (_, i) => ({
    left: `${(i * 23 + 5) % 100}%`, delay: `${(i % 8) * 0.6}s`, dur: `${5 + (i % 4)}s`, size: 18 + (i % 4) * 8, dx: `${((i % 5) - 2) * 20}px`,
  })), []);
  return (
    <div data-testid="thanks-anim-friendly" className="relative min-h-[100dvh] overflow-hidden bg-gradient-to-b from-pink-50 via-rose-50 to-white flex flex-col items-center justify-center px-6 text-center">
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {hearts.map((h, i) => (
          <svg key={i} viewBox="0 0 24 24" className="ask-heart text-pink-400" style={{ left: h.left, width: h.size, animationDelay: h.delay, animationDuration: h.dur, "--dx": h.dx }}>
            <path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 3 4.5 6.7 4.5c2.1 0 3.6 1.2 5.3 3.1 1.7-1.9 3.2-3.1 5.3-3.1 3.7 0 5.8 3.9 4.3 7.3C19.5 16.4 12 21 12 21z" />
          </svg>
        ))}
      </div>
      <div className="relative h-44 w-44 text-pink-300">
        <span className="ask-ring" /><span className="ask-ring" style={{ animationDelay: "1.2s" }} />
        <div className="ask-hug relative flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-pink-300 via-rose-400 to-pink-500 shadow-[inset_-10px_-14px_0_rgba(0,0,0,0.1),0_24px_40px_rgba(236,72,153,0.35)]">
          <span className="text-7xl" aria-hidden>🤗</span>
        </div>
        {[0, 1, 2].map((i) => <span key={i} className="ask-twinkle absolute text-2xl" style={{ top: `${[4, 70, 30][i]}%`, left: `${[90, 95, -10][i]}%`, animationDelay: `${i * 0.6}s` }} aria-hidden>✨</span>)}
      </div>
      <h1 className="ask-rise mt-10 font-display text-4xl font-extrabold tracking-tight text-rose-950" style={{ animationDelay: ".2s" }}>
        A big warm hug{to ? `, ${to}` : ""}
      </h1>
      <p className="ask-rise mt-3 text-base font-semibold text-rose-900/70" style={{ animationDelay: ".4s" }}>From {from}, with all the love.</p>
    </div>
  );
}

function Formal({ to, from }) {
  return (
    <div data-testid="thanks-anim-formal" className="relative min-h-[100dvh] overflow-hidden bg-[#FBF8F1] flex flex-col items-center justify-center px-6 text-center">
      <div className="relative h-52 w-52">
        <svg viewBox="0 0 200 200" className="ask-spin-slow absolute inset-0 text-amber-600/70" aria-hidden>
          {Array.from({ length: 24 }, (_, i) => (
            <ellipse key={i} cx="100" cy="14" rx="5" ry="12" fill="currentColor" transform={`rotate(${i * 15} 100 100)`} opacity={i % 2 ? 0.55 : 1} />
          ))}
        </svg>
        <div className="ask-gold absolute inset-7 rounded-full p-[3px] shadow-[0_20px_40px_rgba(180,83,9,0.35)]">
          <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-[#0D4736]">
            <svg viewBox="0 0 24 24" className="h-12 w-12 text-amber-300" aria-hidden><path fill="currentColor" d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17l-6.1 3.4 1.5-6.8L2.2 9l6.9-.7z" /></svg>
            <span className="mt-1 font-display text-[11px] font-bold uppercase tracking-[0.25em] text-amber-200">With thanks</span>
          </div>
        </div>
      </div>
      <h1 className="ask-rise mt-10 font-display text-4xl font-extrabold tracking-tight ask-gold-text" style={{ animationDelay: ".2s" }}>
        With sincere gratitude
      </h1>
      <p className="ask-rise mt-3 text-base font-semibold text-stone-600" style={{ animationDelay: ".4s" }}>
        {to ? `${to}, ` : ""}your kindness is truly appreciated. — {from}
      </p>
    </div>
  );
}

export default function ThanksAnimation({ style, to, from }) {
  const props = { to, from: from || "A friend" };
  if (style === "friendly") return <Friendly {...props} />;
  if (style === "formal") return <Formal {...props} />;
  return <Playful {...props} />;
}
